import { useCallback, useEffect, useState } from 'react'
import {
  getFeedbacksAdmin,
  updateFeedbackAdmin,
  bulkUpdateFeedbacksAdmin,
  type FeedbackItem,
} from '@/api/adminApi'
import { AdminPage, AdminPanel } from '@/components/Admin'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Admin.module.css'

type Filtro = 'all' | 'pendente' | 'aprovado' | 'oculto'

export function AdminFeedbacksPage() {
  const [list, setList] = useState<FeedbackItem[]>([])
  const [filtro, setFiltro] = useState<Filtro>('all')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [bulkSaving, setBulkSaving] = useState(false)
  const { notifySaved, notifyError } = useSaveFeedback()

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    setSelected(new Set())
    getFeedbacksAdmin(filtro)
      .then(setList)
      .catch((e) => setError(e instanceof Error ? e.message : 'Erro ao carregar'))
      .finally(() => setLoading(false))
  }, [filtro])

  useEffect(() => {
    load()
  }, [load])

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selected.size === list.length) setSelected(new Set())
    else setSelected(new Set(list.map((i) => i.id)))
  }

  const patch = async (
    id: number,
    body: Partial<{ aprovado: boolean; ativo: boolean; destaque: boolean }>
  ) => {
    setSavingId(id)
    try {
      const updated = await updateFeedbackAdmin(id, body)
      setList((prev) => prev.map((i) => (i.id === id ? updated : i)))
      notifySaved('Feedback actualizado.')
    } catch (e) {
      notifyError(e, 'Erro ao atualizar')
      setError(e instanceof Error ? e.message : 'Erro ao atualizar')
    } finally {
      setSavingId(null)
    }
  }

  const bulkApprove = async () => {
    if (selected.size === 0) return
    setBulkSaving(true)
    try {
      const updated = await bulkUpdateFeedbacksAdmin([...selected], { aprovado: true })
      notifySaved(`${updated} feedback(s) aprovado(s).`)
      load()
    } catch (e) {
      notifyError(e, 'Erro na aprovação em massa')
      setError(e instanceof Error ? e.message : 'Erro na aprovação em massa')
    } finally {
      setBulkSaving(false)
    }
  }

  return (
    <AdminPage
      title="Feedbacks que Amamos"
      subtitle="Aprove, oculte e destaque testemunhos para exibir no app do cliente."
      loading={loading}
      error={error}
      wide
    >
      <div className={styles.moderationToolbar}>
        <div className={styles.formGroup}>
          <label htmlFor="fb-filtro">Filtrar</label>
          <select id="fb-filtro" value={filtro} onChange={(e) => setFiltro(e.target.value as Filtro)}>
            <option value="all">Todos</option>
            <option value="pendente">Pendentes</option>
            <option value="aprovado">Aprovados</option>
            <option value="oculto">Ocultos</option>
          </select>
        </div>
        {selected.size > 0 && (
          <button type="button" className={styles.btnSalvar} onClick={bulkApprove} disabled={bulkSaving}>
            {bulkSaving ? 'A aprovar…' : `Aprovar seleccionados (${selected.size})`}
          </button>
        )}
      </div>

      <AdminPanel title={`Testemunhos (${list.length})`}>
        {list.length === 0 ? (
          <p className={styles.listEmpty}>Nenhum feedback encontrado.</p>
        ) : (
          <>
            <div className={styles.bulkSelectRow}>
              <label className={styles.checkboxInline}>
                <input
                  type="checkbox"
                  checked={selected.size === list.length && list.length > 0}
                  onChange={toggleSelectAll}
                />
                Seleccionar todos
              </label>
            </div>
            <div className={styles.listScrollArea}>
              <ul className={styles.list}>
              {list.map((item) => (
                <li key={item.id} className={styles.listItem}>
                  <input
                    type="checkbox"
                    className={styles.listItemCheckbox}
                    checked={selected.has(item.id)}
                    onChange={() => toggleSelect(item.id)}
                    aria-label={`Seleccionar feedback de ${item.nome}`}
                  />
                  <div className={styles.listItemContent}>
                    <div className={styles.listItemTitle}>
                      {item.nome} {item.destaque ? '⭐' : ''}
                    </div>
                    <p className={styles.listItemMeta}>{item.texto}</p>
                    <p className={styles.listItemMeta}>
                      {item.aprovado ? 'Aprovado' : 'Pendente'} · {item.ativo ? 'Ativo' : 'Oculto'}
                    </p>
                  </div>
                  <div className={styles.listItemActions}>
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      onClick={() => patch(item.id, { aprovado: !item.aprovado })}
                      disabled={savingId === item.id}
                    >
                      {item.aprovado ? 'Desaprovar' : 'Aprovar'}
                    </button>
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      onClick={() => patch(item.id, { destaque: !item.destaque })}
                      disabled={savingId === item.id}
                    >
                      {item.destaque ? 'Tirar destaque' : 'Destacar'}
                    </button>
                    <button
                      type="button"
                      className={styles.btnDanger}
                      onClick={() => patch(item.id, { ativo: !item.ativo })}
                      disabled={savingId === item.id}
                    >
                      {item.ativo ? 'Ocultar' : 'Reativar'}
                    </button>
                  </div>
                </li>
              ))}
              </ul>
            </div>
          </>
        )}
      </AdminPanel>
    </AdminPage>
  )
}
